import React, { useState, useEffect } from 'react';
import { X, Share } from 'lucide-react';
import { Button } from './ui/button';

export const InstallBanner = () => {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed or already installed as standalone
    if (localStorage.getItem('pwa-banner-dismissed')) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Only show on mobile screens
    if (window.innerWidth >= 768) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());
    setIsIOS(ios);

    if (ios) {
      // iOS Safari doesn't fire beforeinstallprompt — show manual instructions
      setShow(true);
    } else {
      // Chrome/Android: wait for the browser's install prompt event
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShow(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      dismiss();
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem('pwa-banner-dismissed', '1');
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="pwa-install-banner"
    >
      <div className="flex items-center gap-3 p-3 max-w-lg mx-auto">
        <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 shadow-sm border border-slate-100">
          <img src="/logo192.png" alt="Shakti Ops" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 leading-tight">Add Shakti Ops to Home Screen</p>
          {isIOS ? (
            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
              Tap <Share className="w-3 h-3 text-blue-500 inline flex-shrink-0" /> then "Add to Home Screen"
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-0.5">Install for quick one-tap access</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!isIOS && deferredPrompt && (
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-blue-600 hover:bg-blue-700 h-8 text-xs px-3"
              data-testid="pwa-install-btn"
            >
              Install
            </Button>
          )}
          <button
            onClick={dismiss}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Dismiss"
            data-testid="pwa-banner-dismiss"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>
    </div>
  );
};
